import { useMemo, useState } from 'react'

import { fileTypes } from 'commons/utils/file'
import {
  ActionTabs,
  Badge,
  Checkbox,
  Divider,
  FileErrorToast,
  Flex,
  Icon,
  type ItemActionTabsType,
  LoadingHavaianas,
  Modal,
  Page,
  Select,
  SlimButton,
  Text,
  UploadModal,
} from 'components'
import { FairShareFileTemplateUrl, FairShareLayer, fairShareLevelOptions } from 'constants/enums'
import {
  CalendarCreationModal,
  CalendarCreationModalAction,
} from 'container/SalesCalendar/components/CalendarCreationModal'
import { t } from 'i18next'
import { Trans } from 'react-i18next'
import { toast } from 'react-toastify'
import {
  downloadSharesBMU,
  downloadSharesOffice,
  fetchSharesOffice,
  openOfficeWallets,
  resetShared,
  setOpenUploadMetaModal,
  setOpenUploadShareModal,
  updateFairShareBMU,
  updateFairShareMeta,
  updateFairShareOffice,
  updateMaterialLevel,
} from 'store/FairShareContext/fairShareSlice'
import { useDispatch, useSelector } from 'store/store'

// import { useAppTheme } from 'styles/theme'

import { BMUTrackingModal } from './components/BMUTrackingModal'
import { BMUView } from './components/BMUView'
import { OfficeTrackingModal } from './components/OfficeTrackingModal'
import { OfficeView } from './components/OfficeView'
import * as S from './styles'

export const FairShare = () => {
  // const { colors } = useAppTheme()
  const dispatch = useDispatch()
  const [openWalletsModal, setOpenWalletsModal] = useState(false)
  const [openCalendarCreationModal, setOpenCalendarCreationModal] = useState(false)
  const [selectedOfficeWallets, setSelectedOfficeWallets] = useState<string[]>([])
  const [editLevelModal, setEditLevelModal] = useState(false)
  const [selectedLevel, setSelectedLevel] = useState<string>('')
  const [openTrackingModal, setOpenTrackingModal] = useState(false)

  const {
    loadingBMUDownload,
    loadingShareUpload,
    loadingMetaUpload,
    loadingOfficeDownload,
    loadingOpenWallets,
    loadingLevelUpdate,
    officeFilters,
    sharesBMU,
    sharesOffice,
    loadingBMUView,
    loadingOfficeView,
    openUploadShareModal,
    openUploadMetaModal,
  } = useSelector((state) => state.fairShare)
  const { loadingSaveCalendar } = useSelector((state) => state.salesCalendar)

  enum FairShareTab {
    BMU = '0',
    OFFICE = '1',
  }

  const [fairshareView, setFairshareView] = useState<FairShareTab>(FairShareTab.BMU)

  const handleTabChange = (tab: FairShareTab) => {
    setFairshareView(tab)
    dispatch(resetShared())
  }

  const FairShareTabs: ItemActionTabsType[] = [
    { label: t('common.bmus'), onClick: () => handleTabChange(FairShareTab.BMU) },
    {
      label: t('common.office'),
      onClick: () => handleTabChange(FairShareTab.OFFICE),
    },
  ]

  const handleUploadClick = () => {
    dispatch(setOpenUploadShareModal(true))
  }

  const handleDownload = () => {
    if (fairshareView === FairShareTab.BMU) {
      dispatch(downloadSharesBMU())
    }
    if (fairshareView === FairShareTab.OFFICE) {
      dispatch(downloadSharesOffice())
    }
  }

  const toastCallback = (file: ArrayBuffer) => {
    const fileName = 'erro_upload_fairshare.xlsx'
    toast.error(<FileErrorToast file={file} fileName={fileName} />, { autoClose: false })
  }

  const toastCallbackMeta = (file: ArrayBuffer) => {
    const fileName = 'erro_upload_meta.xlsx'
    toast.error(<FileErrorToast file={file} fileName={fileName} />, { autoClose: false })
  }

  const handleUpload = (file: File) => {
    const formData = new FormData()
    formData.append('file', file)

    if (fairshareView === FairShareTab.BMU) {
      dispatch(updateFairShareBMU(formData, toastCallback))
    }

    if (fairshareView === FairShareTab.OFFICE) {
      dispatch(updateFairShareOffice(formData, toastCallback))
    }
  }

  const handleMetaUploadClick = () => {
    dispatch(setOpenUploadMetaModal(true))
  }

  const handleMetaUpload = (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    dispatch(updateFairShareMeta(formData, toastCallbackMeta))
  }

  const toggleOfficeWalletItem = (officeCode: string) => {
    setSelectedOfficeWallets((prev) =>
      prev.includes(officeCode)
        ? prev.filter((item) => item !== officeCode)
        : [...prev, officeCode],
    )
  }

  const handleWalletsModalClose = () => {
    setOpenWalletsModal(false)
    setSelectedOfficeWallets([])
  }

  const toastCallbackOpenWallets = (file: ArrayBuffer) => {
    const fileName = 'erro_abertura_carteira_fairshare.xlsx'
    toast.error(
      <FileErrorToast
        file={file}
        fileName={fileName}
        messageKey="screens.fairShare.walletFromOfficeProblem"
      />,
      { autoClose: false },
    )
  }

  const handleOpenWallets = () => {
    const payload = {
      businessUnitCode: officeFilters?.businessUnit ?? '',
      officeCode: selectedOfficeWallets,
      yearMonth: officeFilters?.yearMonth ?? 0,
    }
    dispatch(openOfficeWallets(payload, toastCallbackOpenWallets))
    handleWalletsModalClose()
  }

  const handleEditLevelModalClose = () => {
    setSelectedLevel('')
    setEditLevelModal(false)
  }

  const handleEditLevel = () => {
    dispatch(updateMaterialLevel(selectedLevel))
    handleEditLevelModalClose()
  }

  const handleCalendarCreationSuccess = () => {
    if (officeFilters) {
      dispatch(fetchSharesOffice(officeFilters))
    }
  }

  const getShareTemplateUrl = () =>
    fairshareView === FairShareTab.BMU
      ? `${process.env.REACT_APP_DOWNLOAD_FILES_BASE_URL}${FairShareFileTemplateUrl.BMU}`
      : `${process.env.REACT_APP_DOWNLOAD_FILES_BASE_URL}${FairShareFileTemplateUrl.OFFICE}`

  const selectedMaterials = useMemo(() => {
    return sharesOffice.data.filter((item) => item.checked)
  }, [sharesOffice])

  const levelOptionsLayer = useMemo(() => {
    if (officeFilters?.regionCode) return FairShareLayer.REGION
    if (officeFilters?.teamCode) return FairShareLayer.TEAM
    return FairShareLayer.OFFICE
  }, [officeFilters])

  const hasShareData =
    fairshareView === FairShareTab.BMU ? sharesBMU.data.length > 0 : sharesOffice.data.length > 0

  const isLoadingView = fairshareView === FairShareTab.BMU ? loadingBMUView : loadingOfficeView

  return (
    <Page
      fullwidth
      renderHeaderComponent={
        <S.TitleBar>
          <S.Title>{t('screens.fairShare.title')}</S.Title>
        </S.TitleBar>
      }
    >
      <S.Container>
        <Flex direction="column" gap="xm">
          <Flex alignItems="center" fullWidth justifyContent="space-between">
            <Flex gap="sm">
              <ActionTabs currentItem={fairshareView} items={FairShareTabs} variant="contained" />
              <Badge appearance="soft" color="success">
                {t('screens.fairShare.currentShareView')}
              </Badge>
            </Flex>

            {hasShareData && (
              <Flex alignItems="center" gap="sm">
                {fairshareView === FairShareTab.OFFICE && (
                  <SlimButton
                    content={t('common.level')}
                    disabled={isLoadingView}
                    icon={<Icon icon="pencil" size="18" />}
                    loading={loadingLevelUpdate}
                    onClick={() => setEditLevelModal(true)}
                    variant="border"
                  />
                )}

                <SlimButton
                  content={t('buttons.report')}
                  disabled={isLoadingView}
                  icon={<Icon icon="download" size="18" />}
                  loading={loadingBMUDownload || loadingOfficeDownload}
                  onClick={handleDownload}
                  variant="border"
                />

                <SlimButton
                  content={t('buttons.tracking')}
                  disabled={isLoadingView}
                  icon={<Icon icon="track" size="16" />}
                  onClick={() => setOpenTrackingModal(true)}
                  variant="border"
                />

                {fairshareView === FairShareTab.OFFICE && (
                  <SlimButton
                    content={t('screens.fairShare.openWallets')}
                    disabled={
                      isLoadingView || sharesOffice.offices.every((office) => office.frozenTarget)
                    }
                    icon={<Icon icon="wallet-card" size="18" />}
                    loading={loadingOpenWallets}
                    onClick={() => setOpenWalletsModal(true)}
                    variant="border"
                  />
                )}

                <Divider length="28px" orientation="vertical" />

                {fairshareView === FairShareTab.BMU && (
                  <SlimButton
                    content={t('screens.fairShare.editShare')}
                    disabled={isLoadingView}
                    icon={<Icon icon="file-input" size="18" />}
                    loading={loadingShareUpload}
                    onClick={handleUploadClick}
                    variant="border"
                  />
                )}

                {fairshareView === FairShareTab.OFFICE && (
                  <Flex alignItems="center" gap="sm">
                    <Flex alignItems="center" gap="xs">
                      <Icon icon="file-input" size="18" />
                      <Text color="strong" size="xs">
                        {t('buttons.edit')}:
                      </Text>
                    </Flex>

                    <SlimButton
                      content={t('common.target')}
                      disabled={isLoadingView || loadingShareUpload}
                      loading={loadingMetaUpload}
                      onClick={handleMetaUploadClick}
                      variant="border"
                    />

                    <SlimButton
                      content={t('screens.fairShare.share')}
                      disabled={isLoadingView || loadingMetaUpload}
                      loading={loadingShareUpload}
                      onClick={handleUploadClick}
                      variant="border"
                    />
                  </Flex>
                )}
              </Flex>
            )}
          </Flex>
          <Divider />

          {fairshareView === FairShareTab.BMU ? <BMUView /> : <OfficeView />}
        </Flex>
      </S.Container>

      <Modal
        actionBadge={
          <Badge appearance="soft" color="warning">
            {t('common.messages.actionCannotBeUndone')}
          </Badge>
        }
        buttonAction={handleOpenWallets}
        buttonDisabled={
          selectedOfficeWallets.length === 0 || loadingOfficeView || loadingSaveCalendar
        }
        buttonText={t('buttons.open')}
        closeAction={handleWalletsModalClose}
        modalWidth={550}
        open={openWalletsModal}
        returnAction={handleWalletsModalClose}
        setOpen={setOpenWalletsModal}
        showAction
        showClose
        showReturn
        title={t('screens.fairShare.openWallets')}
        titleSize={18}
      >
        <Flex direction="column" fullWidth gap="md">
          {loadingOfficeView || loadingSaveCalendar ? (
            <LoadingHavaianas />
          ) : (
            <>
              <Text color="stronger" size="xs">
                {t('screens.fairShare.chooseOfficeToOpenWallet')}
              </Text>

              <Flex gap="md">
                {sharesOffice.offices
                  .filter((office) => !office.frozenTarget)
                  .map((office) => (
                    <Flex alignItems="center" key={office.officeCode}>
                      <Checkbox
                        checked={selectedOfficeWallets.includes(office.officeCode)}
                        disabled={!office.hasCalendar}
                        key={office.officeCode}
                        onChange={() => toggleOfficeWalletItem(office.officeCode)}
                        size="small"
                      />
                      <Text color={!office.hasCalendar ? 'soft' : 'stronger'} size="xs" weight="sm">
                        {office.officeCode}
                      </Text>
                    </Flex>
                  ))}
              </Flex>

              {/* TODO: UNCOMMENT ON SALES CALENDAR SCREEN DEPLOY*/}

              {/* {sharesOffice.offices.some(
                (office) => !office.frozenTarget && !office.hasCalendar,
              ) && (
                <>
                  <Divider />
                  <S.CalendarInfoContainer alignItems="center" fullWidth gap="md">
                    <S.IconContainer>
                      <Icon color={colors.ion.primary.onsoft} icon="calendar-plus" size="20" />
                    </S.IconContainer>
                    <Flex direction="column" gap="xs">
                      <Text customColor={colors.ion.info.onsofter} size="xs" weight="md">
                        {t('screens.fairShare.disabledOfficesDescription')}
                      </Text>
                      <S.LinkWrapper onClick={() => setOpenCalendarCreationModal(true)}>
                        <Text customColor={colors.ion.info.main} decoration="underline" size="xs">
                          {t('screens.fairShare.clickToAdd')}
                        </Text>
                      </S.LinkWrapper>
                    </Flex>
                  </S.CalendarInfoContainer>
                </>
              )} */}
            </>
          )}
        </Flex>
      </Modal>

      <Modal
        actionBadge={
          !selectedLevel ? (
            <Badge appearance="soft" color="neutral">
              {t('common.messages.noChangesDone')}
            </Badge>
          ) : (
            <></>
          )
        }
        buttonAction={handleEditLevel}
        buttonDisabled={!selectedLevel}
        buttonText={t('buttons.save')}
        closeAction={handleEditLevelModalClose}
        modalWidth={550}
        open={editLevelModal}
        returnAction={handleEditLevelModalClose}
        setOpen={setEditLevelModal}
        showAction
        showClose
        showReturn
        title={t('screens.fairShare.editLevel')}
        titleSize={18}
      >
        <Flex direction="column" fullWidth gap="md">
          <Text color="stronger" size="xs">
            <Trans
              context={selectedMaterials.length ? '' : 'all'}
              count={selectedMaterials.length}
              i18nKey="screens.fairShare.editingLevel"
            />
          </Text>
          <Flex fullWidth gap="md">
            <Select
              label={t('common.level')}
              menuText={t('common.placeholders.select')}
              name="level"
              onChange={(e) => {
                const value = e.target.value
                setSelectedLevel(String(value))
              }}
              options={fairShareLevelOptions
                .filter((option) => option.layers.includes(levelOptionsLayer))
                .map((option) => ({
                  title: t(option.title),
                  value: option.value,
                }))}
              value={selectedLevel}
            />
          </Flex>
        </Flex>
      </Modal>

      {openCalendarCreationModal && (
        <CalendarCreationModal
          action={CalendarCreationModalAction.CREATE}
          defaultOffice={sharesOffice.offices.find((office) => !office.hasCalendar)?.officeCode}
          defaultShareMonth={officeFilters?.yearMonth}
          disableYearMonth
          onCreationSuccess={handleCalendarCreationSuccess}
          open={openCalendarCreationModal}
          setOpen={setOpenCalendarCreationModal}
        />
      )}

      {openUploadShareModal && (
        <UploadModal
          accept={fileTypes.Excel}
          description={t('screens.fairShare.uploadShareDescription')}
          onConfirm={handleUpload}
          open={openUploadShareModal}
          setOpen={(open) => dispatch(setOpenUploadShareModal(open))}
          templateUrl={getShareTemplateUrl()}
          title={
            hasShareData ? t('screens.fairShare.editShare') : t('screens.fairShare.uploadShare')
          }
        />
      )}

      {openUploadMetaModal && (
        <UploadModal
          accept={fileTypes.Excel}
          description={t('screens.fairShare.uploadShareDescription')}
          onConfirm={handleMetaUpload}
          open={openUploadMetaModal}
          setOpen={(open) => dispatch(setOpenUploadMetaModal(open))}
          templateUrl={`${process.env.REACT_APP_DOWNLOAD_FILES_BASE_URL}${FairShareFileTemplateUrl.META}`}
          title={hasShareData ? t('screens.fairShare.editMeta') : t('screens.fairShare.loadMeta')}
        />
      )}

      {fairshareView === FairShareTab.BMU && openTrackingModal && (
        <BMUTrackingModal open={openTrackingModal} setOpen={setOpenTrackingModal} />
      )}

      {fairshareView === FairShareTab.OFFICE && openTrackingModal && (
        <OfficeTrackingModal open={openTrackingModal} setOpen={setOpenTrackingModal} />
      )}
    </Page>
  )
}
